// src/components/auth/RecoverySurface.tsx
//
// H-2C2 WP-6 — the deterministic recovery surface.
//
// Presentational only. Given a canonical `RecoveryState` it renders the
// matching non-technical copy and exactly ONE action. It derives nothing:
// `RpBootGate` decides the state and which action to offer — a sign-in
// restart, a bounded retry, or (for a TERMINAL state that re-authenticating
// the same account cannot escape) a full sign-out.

import { useState } from 'react';
import { initiateCentralLogout } from '../../config/centralAuth';
import { RECOVERY_COPY, type SurfaceState } from './recoveryStates';

/** Where a fresh sign-in restarts the RP authorization-code flow. */
export const AUTH_START_PATH = '/auth/start';

interface RecoverySurfaceProps {
  recovery: SurfaceState;
  /**
   * `signin` → restart the flow at `/auth/start` (re-authenticate the SAME
   *            account — correct for an expired/interrupted session);
   * `retry`  → re-run the boot fetch (offered ONLY for an exhausted
   *            `degraded` retry budget / `auth_unavailable` — never else);
   * `logout` → end the current session via the canonical Central logout, so
   *            the user lands on a clean login and can sign in with a
   *            DIFFERENT account. Offered ONLY for a terminal state where
   *            re-authenticating the same account returns to the same dead
   *            end (`access_revoked`, `workspace_closed`) — otherwise the
   *            "Sign in" action just loops.
   */
  action: 'signin' | 'retry' | 'logout';
  /** Invoked by the `retry` action. */
  onRetry?: () => void;
}

const BUTTON_CLASS =
  'inline-flex items-center justify-center rounded-md bg-blue-600 px-4 py-2 ' +
  'text-sm font-medium text-white hover:bg-blue-700 focus:outline-none ' +
  'focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ' +
  'disabled:opacity-60 disabled:cursor-not-allowed';

export default function RecoverySurface({
  recovery,
  action,
  onRetry,
}: RecoverySurfaceProps) {
  const copy = RECOVERY_COPY[recovery];
  const [signingOut, setSigningOut] = useState(false);
  const [logoutError, setLogoutError] = useState<string | null>(null);

  // Canonical, fail-closed logout. `initiateCentralLogout` navigates the
  // window on success (execution typically stops before we return); if it
  // throws we surface the message and re-enable the button so the user is
  // never stranded on a dead action. Mirrors SignOutQuickAction.
  const handleLogout = async () => {
    if (signingOut) return;
    setLogoutError(null);
    setSigningOut(true);
    try {
      await initiateCentralLogout();
    } catch (err) {
      setLogoutError(err instanceof Error ? err.message : 'Sign out failed');
      setSigningOut(false);
    }
  };

  return (
    <div
      data-testid="recovery-surface"
      data-recovery={recovery}
      role="alert"
      className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-6"
    >
      <div className="max-w-md w-full text-center">
        <h1 className="text-xl font-semibold text-gray-900">{copy.title}</h1>
        <p className="mt-3 text-sm text-gray-600">{copy.message}</p>
        <div className="mt-6">
          {action === 'retry' ? (
            <button
              type="button"
              data-testid="recovery-retry"
              onClick={onRetry}
              className={BUTTON_CLASS}
            >
              Try Again
            </button>
          ) : action === 'logout' ? (
            <button
              type="button"
              data-testid="recovery-logout"
              onClick={handleLogout}
              disabled={signingOut}
              className={BUTTON_CLASS}
            >
              {signingOut ? 'Signing out…' : copy.signInCta}
            </button>
          ) : (
            <a data-testid="recovery-signin" href={AUTH_START_PATH} className={BUTTON_CLASS}>
              {copy.signInCta}
            </a>
          )}
          {action === 'logout' && logoutError && (
            <p
              role="alert"
              data-testid="recovery-logout-error"
              className="mt-3 text-xs text-red-600"
            >
              {logoutError}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
